import { z } from "zod";
import { PaperInfo } from "./paper.js";
import { BillingTiming, PlanBrief } from "./installment.js";
import { COMPANY_MSG, CompanyUpdate } from "./company.js";
import { AwaitingPayment, UnpaidFigures } from "./payment.js";
import { HomePurchasesDue } from "./purchase.js";
import { RoundingCode } from "./rounding.js";
import { AxesInfo, LayoutAxis, LineState, NoteKind, WorkType } from "./estimateAxes.js";
import { LumpsumBasis, LumpsumWarnings, QuantitySource, TakeoffSummary } from "./takeoff.js";
import { CostDefault, CostSummary, TakeoffSheetInfo } from "./estimatePc.js";
import { ProjectChoice } from "./ongoing.js";
import { HomeRfqWaiting } from "./rfq.js";
import { HomeClosingDue } from "./closing.js";
import { HomeRecurringDrafts } from "./recurring.js";
import { TaxCategory, TaxDate, TaxSummary } from "./tax.js";
import { MarkupSettings, ReferralInfo, ReferralPct, ReferralSummary } from "./referral.js";

// ---------------------------------------------------------------------------
// 見積（縦串2b：表紙だけの見積を1枚、下書き）。docs/10 §9.1・docs/17。
// 行の種類は7つ（DB の row_role）：detail=ふつう／group=見出し／note=文章／subtotal=小計／
// overhead_rate=率／excluded=別途／discount=値引き。計算は DB（014 fn_recalc_revision・017 fn_recalc_taxes）が行う
// ---------------------------------------------------------------------------

export const ESTIMATE_MSG = {
  notFound: "その見積は見つかりません",
  notDraft: "出したあとの見積は書き換えられません。修正するときは訂正版として新しく出します",
  clientRequired: "{customer}を選んでください",
  subjectTooLong: "{subject}は200文字までです",
  rateRequired: "率の行には率（%）と基礎が要ります",
  tooManyLines: "行は1枚に500行までです",
  targetDiscountOnce: "総額指定の値引きは1つの見積に1つだけ置けます",
  sheetsNeedCover: "内訳に分けるときは表紙が1つ要ります",
  sheetUnknown: "行が、無い内訳の紙を指しています",
  sheetsTooMany: "内訳は50までです",
  /** 039：見積は必ずどれかの案件に入る（docs/28） */
  groupRequired: "{project}を選んでください（新しい{project}にもできます）",
  staffTooLong: "担当者は50文字までです",
} as const;

export const RowRole = z.enum(["detail", "group", "note", "subtotal", "overhead_rate", "excluded", "discount"]);
export type RowRole = z.infer<typeof RowRole>;

/** 行の区分（#131 の2回目：名詞1語。説明は ROW_ROLE_HELP＝「？」の中） */
export const ROW_ROLE_LABEL: Record<RowRole, string> = {
  detail: "明細",
  group: "見出し",
  note: "説明",
  subtotal: "小計",
  overhead_rate: "率",
  excluded: "別途",
  discount: "値引き",
};
/** 区分の「？」の中の文（#131 の2回目） */
export const ROW_ROLE_HELP: Partial<Record<RowRole, string>> = {
  subtotal: "小計：前の小計の次からを合計します。",
  overhead_rate: "率：基礎 × 率（諸経費 10% など）。基礎は 枠・選んだ小計・労務費・選んだ行。",
  excluded: "別途：金額欄に「別途」と印字し、合計に入れません。",
  discount: "値引き：マイナスの金額。総合計（税抜）を決めた額にもできます（端数調整）。",
};
/** 区分の「？」の最後の1行（#215：金額の欄の placeholder「金額 / 10% / 別途」をやめて「？」へ） */
export const ROW_ROLE_HELP_AMT = "数量と単価が空のとき、金額の欄に 金額（1式）・10%（率）・別途 と打つと行の種類が決まります。";
/** 率の「？」（モジュール field の無い会社＝基礎に「労務費」が無い。#142 段2） */
export const ROW_ROLE_HELP_RATE_NO_LABOR = "率：基礎 × 率（諸経費 10% など）。基礎は 枠・選んだ小計・選んだ行。";

/** 率の基礎（014 rate_base） */
export const RateBase = z.enum(["block", "subtotal", "labor", "lines"]);
export type RateBase = z.infer<typeof RateBase>;
export const RATE_BASE_LABEL: Record<RateBase, string> = {
  block: "枠の合計に対して（前の小計から次の小計まで）",
  subtotal: "選んだ小計に対して",
  labor: "労務費に対して",
  lines: "選んだ行に対して",
};

/** 何の費用か（改正建設業法の5項目分離、014 cost_kind） */
export const CostKind = z.enum(["statutory_welfare", "safety_health", "kentaikyo", "overhead", "other"]);
export type CostKind = z.infer<typeof CostKind>;
export const COST_KIND_LABEL: Record<CostKind, string> = {
  statutory_welfare: "法定福利費",
  safety_health: "安全衛生経費",
  kentaikyo: "建退共掛金",
  overhead: "諸経費（現場管理費・一般管理費）",
  other: "その他",
};

export const EXCLUDED_WORDS = ["別途", "サービス", "含む", "込", "無料"] as const;

const Yen = z.number().int().min(-999_999_999_999).max(999_999_999_999);

/**
 * {order}の行の内訳（#233、docs/93 §4.3、改正建設業法の5項目のうち4つ＝建退共掛金は2回目）：材料費・労務費・法定福利費・安全衛生経費（円・0 以上・任意）。
 * field の会社の画面だけに出す（ほかの会社は値を隠すだけ＝保存で消さない）。DB は document_lines.labor_breakdown
 */
const LaborYen = z.number().int().min(0).max(99_999_999_999).nullable().optional();
export const LaborBreakdown = z.object({ material: LaborYen, labor: LaborYen, welfare: LaborYen, safety: LaborYen });
export type LaborBreakdown = z.infer<typeof LaborBreakdown>;
export const LABOR_KEYS = ["material", "labor", "welfare", "safety"] as const;
export const LABOR_LABEL: Record<(typeof LABOR_KEYS)[number], string> = { material: "材料費", labor: "労務費", welfare: "法定福利費", safety: "安全衛生経費" };
/** 内訳の合計（入れた欄だけ。どれも空なら null） */
export const laborSum = (b: LaborBreakdown | null | undefined): number | null => {
  if (!b) return null;
  const xs = LABOR_KEYS.map((k) => b[k]).filter((v): v is number => typeof v === "number");
  return xs.length ? xs.reduce((a, v) => a + v, 0) : null;
};

/** 画面が送る行。id は画面が決める（uuid）。金額（amount）は DB が計算して返す */
export const LineInput = z.object({
  id: z.string().uuid(),
  role: RowRole,
  name: z.string().max(200).default(""),
  spec: z.string().max(200).default(""),
  note: z.string().max(200).default(""),
  quantity: z.number().min(0).max(99_999_999).multipleOf(0.01).nullable().default(null),
  unit: z.string().max(20).default(""),
  unitPrice: Yen.nullable().default(null),
  /** 値引きの行だけ：直接打った金額（マイナス）。総額指定があればそちらが優先 */
  amount: Yen.nullable().default(null),
  /** 率の行：0.10 = 10% */
  rate: z.number().min(0).max(9.9999).nullable().default(null),
  rateBase: RateBase.nullable().default(null),
  rateBaseLineIds: z.array(z.string().uuid()).default([]),
  /** 別途の行：金額欄に印字する文字 */
  amountLabel: z.string().max(20).nullable().default(null),
  /** 値引きの行：総合計（税抜）をこの額にする */
  discountTargetTotal: Yen.nullable().default(null),
  isLabor: z.boolean().default(false),
  costKind: CostKind.nullable().default(null),
  /** 社内用：仕入れ（原価）。空なら「分からない」（ルール7）。負もあり得る（049：値引きを「まとめて」入れた行） */
  costAmount: Yen.nullable().default(null),
  /**
   * 仕入れの出どころ「自社」（#131 の2回目）：自社で施工する＝仕入れ 0 として扱い、「仕入れ未入力」に数えない。ふつうの行だけ。
   * 外注先（vendorPartnerId）が付いている行は自社にならない（API が外す）。仕入れの数字を打つと手入力になる
   */
  costOwn: z.boolean().default(false),
  /** どの紙の行か（estimate_sheets.id）。内訳を使わない見積は null。表紙の行は表紙の紙の id */
  sheetId: z.string().uuid().nullable().default(null),
  /** 場所（#129。その見積の estimate_places）。紙には出ない（並べ方の見出しにだけ使う） */
  placeId: z.string().uuid().nullable().default(null),
  /** 工種（#129。会社の trades） */
  tradeId: z.string().uuid().nullable().default(null),
  /** 材工区分（#129）。空でよい */
  workType: WorkType.nullable().default(null),
  /** 仮／確定（#129）。スマホで作った行は仮。社内で型番・単価を決めたら確定 */
  lineState: LineState.default("final"),
  /** 仮のときの大まかな書き方（「75φ 60W相当」）。確定しても残す。紙には出ない */
  provisionalNote: z.string().max(200).nullable().default(null),
  /** 外注先（#129。顧客｜外注先 の vendor・both）。紙には出ない */
  vendorPartnerId: z.string().uuid().nullable().default(null),
  /** 注記の種類（#129。文章だけの行・別途の行だけ）：scope＝範囲の注記（表紙の下に一覧）／plain＝ただの文章 */
  noteKind: NoteKind.nullable().default(null),
  /**
   * 数量の出どころ（#130）。takeoff＝拾い出しの合計（数量は DB が決める。送った quantity は使わない）。ふつうの行だけ。
   * 請求書・領収書・雛形の行はいつも manual
   */
  quantitySource: QuantitySource.default("manual"),
  /** 一式の内訳の行（#130）：親の行（同じ保存の中のふつうの行）の id。親が無ければ API が外す */
  parentLineId: z.string().uuid().nullable().default(null),
  /**
   * 品目（items、#118）。写真の引き出しで作った行は品目を指す（同じ場所 × 同じ品目の行を見つけるため）。紙には出ない。
   * 画面は受け取ったまま送り返す（PC の保存で消えないように）。自社の品目だけ（DB のトリガー）
   */
  itemId: z.string().uuid().nullable().default(null),
  /**
   * 税区分（T1、tax.ts）。新コード7つだけ（古いコード・知らない値は 400）。省けば standard。
   * 画面は受け取ったまま送り返す（PC の保存で標準に戻らないように）。税区分の列は T2
   */
  taxCategory: TaxCategory.optional(),
  /** 行の取引日（T1。税率を決める日。省けば 版の取引日 → 発行日）。締め請求の明細＝元の納品書の納品日など */
  taxDate: TaxDate.nullable().optional(),
  /**
   * パートナーの率（#213、docs/40 §5。％・小数2桁）。null＝見積の率に従う・0＝対象外（定価の商品など）。紙には出ない（社内用）。
   * ふつうの行・率の行・値引きだけ意味がある。画面は受け取ったまま送り返す（パートナー手数料を OFF にした会社の保存で消えないように）
   */
  referralPct: ReferralPct.nullable().optional(),
  /** {order}の行の内訳（#233）。見積・請求書の行は使わない（null）。画面は受け取ったまま送り返す */
  laborBreakdown: LaborBreakdown.nullable().optional(),
});
export type LineInput = z.infer<typeof LineInput>;

/** 内訳の紙（estimate_sheets、002/013/014）。表紙（cover）1つ＋内訳（breakdown）。使わないときは空 */
export const SheetType = z.enum(["cover", "breakdown"]);
export type SheetType = z.infer<typeof SheetType>;
export const SheetInput = z.object({
  id: z.string().uuid(),
  type: SheetType,
  name: z.string().max(100).default(""),
  /** 表紙に並ぶ「この内訳の合計」の行の備考（cover_note） */
  note: z.string().max(200).default(""),
  /** この内訳の紙ごと「労務費」の印（率の基礎「労務費に対して」） */
  isLabor: z.boolean().default(false),
  /**
   * 内訳を使うか（#199、docs/80。estimate_sheets.in_use）。false＝不使用：その紙の行は 合計・消費税・表紙の内訳の合計・率の基礎・
   * 仕入れ・紙（PDF）・紙に出る差分 に入れない（金額は DB が計算する）。中身は見られる・直せる。表紙は不使用にできない（DB の CHECK）
   */
  inUse: z.boolean().default(true),
});
export type SheetInput = z.infer<typeof SheetInput>;

/**
 * 使う内訳の行だけ（#199）：紙の無い行（1枚の見積）・表紙の行・使う内訳の行。不使用の内訳の行を外す。
 * 合計に入れない行を数えないように、仕入れの集計・一式の知らせ・原価割れの数・紙（PDF）の行はこれを通す（金額は DB が計算済み）
 */
export function linesInUse<L extends { sheetId?: string | null | undefined }>(lines: readonly L[], sheets: ReadonlyArray<{ id: string; inUse?: boolean | undefined }>): L[] {
  const off = new Set(sheets.filter((x) => x.inUse === false).map((x) => x.id));
  return off.size ? lines.filter((l) => !l.sheetId || !off.has(l.sheetId)) : lines.slice();
}
/** 使う内訳（#199）：表紙と、不使用でない内訳 */
export function sheetsInUse<S extends { inUse?: boolean | undefined }>(sheets: readonly S[]): S[] {
  return sheets.filter((x) => x.inUse !== false);
}

/**
 * 紙（PDF）に出す形（#199）：不使用の内訳とその行を外す。不使用の内訳が無ければ同じもの（＝紙の HTML は1バイトも変わらない）。
 * 見積書・請求書・領収書・積算表の紙はこれを通してから描く（表紙の「内訳の合計」の行・内訳のページ・軽減の注記も使う内訳だけになる）
 */
export function printableOf<E extends { sheets: ReadonlyArray<{ id: string; inUse?: boolean | undefined }>; lines: ReadonlyArray<{ sheetId?: string | null | undefined }> }>(e: E): E {
  if (!e.sheets.some((x) => x.inUse === false)) return e;
  return { ...e, sheets: sheetsInUse(e.sheets), lines: linesInUse(e.lines, e.sheets) };
}

/** 内訳の複製（#199）：POST /estimates/:id/sheets/:sheetId/copy の本文。version は画面が読んだときの Estimate.updatedAt（違えば 409 ESTIMATE_STALE） */
export const SheetCopyRequest = z.object({ version: z.string().max(40).optional() });
export type SheetCopyRequest = z.infer<typeof SheetCopyRequest>;

export const EstimateCreate = z.object({
  clientId: z.string().uuid({ message: ESTIMATE_MSG.clientRequired }),
  /**
   * 案件（039、docs/28）。見積は必ずどれかの案件に入る。既存の案件ならその id。
   * null（または newGroup: true）なら、この見積のための新しい案件を同時に作る（名前＝工事名。見積が1枚のあいだは工事名に合わせて変わる）
   */
  projectGroupId: z.string().uuid().nullable().default(null),
  /** 新しい案件を同時に作る（projectGroupId が null のときと同じ。はっきり書くとき用） */
  newGroup: z.boolean().optional(),
  /** 顧客担当者（039）。省けば、既存の案件ならその案件の担当者、新しい案件なら無し */
  partnerContactId: z.string().uuid().nullable().optional(),
  /** 自社の担当者（039）。省けば、既存の案件ならその案件の担当者、新しい案件ならログインしている人の氏名 */
  staffName: z.string().trim().max(50, ESTIMATE_MSG.staffTooLong).optional(),
  /** 宛先の支店・営業所（028）。顧客に支店があるときだけ。無ければ null（本社）。省けば、既存の案件ならその案件の支店 */
  partnerBranchId: z.string().uuid().nullable().optional(),
  /** 見積の雛形（023）。渡すと、その雛形の中身（内訳の紙＋行）を新しい下書きの初期値にする。雛形は変わらない */
  fromTemplateId: z.string().uuid().nullable().default(null),
  /** 工事名の初期値（033。案件の画面の「{job}を追加」が渡す。省けば空のまま） */
  subject: z.string().trim().max(200, ESTIMATE_MSG.subjectTooLong).optional(),
  /**
   * {project}の選び方（#289、docs/101 §6・§10）。渡すと projectGroupId・newGroup より先：{project}の id／"new"＝新規（{subject}と同じ）／
   * "ongoing:new"＝{customer}の略称＋{ongoingProject}の継続案件を作る。完了した継続案件に作ると答えに warning（止めない）
   */
  projectId: ProjectChoice.optional(),
  /** 見積日（#289 の作成画面の欄）。省けば今日 */
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type EstimateCreate = z.infer<typeof EstimateCreate>;

/** 下書きの保存（見出しと行をまとめて。行は全部入れ替える） */
export const EstimateSave = z.object({
  clientId: z.string().uuid({ message: ESTIMATE_MSG.clientRequired }),
  /**
   * 案件（018 documents.project_group_id）。039 から必ずどれかの案件に入る（null は 400）。
   * 顧客を変えたときは newGroup: true（新しい顧客の新しい案件に入り直す。空になった自動の案件は DB が消す）
   */
  projectGroupId: z.string().uuid().nullable().default(null),
  newGroup: z.boolean().optional(),
  /** 宛先の支店・営業所（028 documents.partner_branch_id）。NULL＝本社（支店なし） */
  partnerBranchId: z.string().uuid().nullable().default(null),
  /** 顧客担当者（039 documents.partner_contact_id）。NULL＝指定しない（紙の宛名は「会社名 御中」） */
  partnerContactId: z.string().uuid().nullable().default(null),
  subject: z.string().trim().max(200, ESTIMATE_MSG.subjectTooLong).default(""),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** 工事場所（暗号化して版に持つ。案件から写し、打ち直せる。発行時に凍結） */
  siteAddress: z.string().trim().max(200).default(""),
  notes: z.string().max(2000).default(""),
  workPeriodText: z.string().max(100).default(""),
  workOutline: z.string().max(200).default(""),
  validityText: z.string().max(100).default(""),
  staffName: z.string().max(50).default(""),
  /** 差し替えの一文（035）。2回目からの回を直しているときだけ使う（紙の備考の上に出る。空なら出ない）。省いたら変えない */
  replaceSentence: z.string().trim().max(200).optional(),
  /** 帳票の取引日（T1。引渡しの日。行の税率を決める日の既定）。省いたら変えない・null で外す（発行日を使う）。日付の欄は T2 */
  taxPointDate: TaxDate.nullable().optional(),
  /**
   * パートナー（#213、docs/40 §4。モジュール referral の会社だけ＝OFF なら 404）。省いたら変えない・null で外す。
   * 率は見積ごと（★A。省けば付けたときは顧客の登録の率、変えないときは今の率）。行の率（LineInput.referralPct）が null の行はこの率
   */
  referrerId: z.string().uuid().nullable().optional(),
  referrerPct: ReferralPct.nullable().optional(),
  /** 内訳の紙（並び順＝タブの並び）。空なら1枚の紙。あれば表紙が1つ必要で、全部の行の sheetId がここにある id を指す */
  sheets: z.array(SheetInput).max(50, ESTIMATE_MSG.sheetsTooMany).default([]),
  lines: z.array(LineInput).max(500, ESTIMATE_MSG.tooManyLines),
  /**
   * 画面が覚えている系譜（id → lineage_id。037）。直しているときだけ見る。いったん保存で消えた行・紙を
   * 同じ id で戻したとき（トーストの「元に戻す」）に使う。前の回にある系譜で、ほかの行が使っていないものだけ採る
   */
  lineage: z.object({ lines: z.record(z.string().uuid(), z.string().uuid()), sheets: z.record(z.string().uuid(), z.string().uuid()) }).optional(),
  /**
   * 版（#131）：画面が読んだときの Estimate.updatedAt。いまの版と違えば 409 ESTIMATE_STALE（スマホの行の保存などとぶつかった）。
   * #202 で必須に（無ければ 400 VALIDATION。前は省くと見なかった＝ほかの直しを黙って消す抜けがあった）。雛形・請求書は別の保存
   */
  version: z.string({ error: "版（version）を付けて送ってください" }).min(1, "版（version）を付けて送ってください").max(40),
});
export type EstimateSave = z.infer<typeof EstimateSave>;

export const EstimateLine = LineInput.extend({
  lineNo: z.number().int(),
  /** 税区分（T1。出した古いコードの行も新コードに読み替えて返す） */
  taxCategory: TaxCategory,
  taxDate: TaxDate.nullable(),
  /** 行に当てた税率（0.10＝10%。DB が下書きのたびに入れ直し、出すと固まる。T1 より前に出した行と金額の無い行は null） */
  taxRate: z.number().nullable(),
  /** DB が計算した金額（税抜）。別途・見出し・文章は 0 */
  amount: Yen,
  /** 仕入れの出どころ（047、docs/33）：manual＝手入力／vendor_quote＝外注見積から。none は仕入れなし */
  costSource: z.enum(["none", "manual", "vendor_quote", "standard_unit_price", "ai_estimated", "own"]).default("none"),
  /**
   * 外注見積から入れたとき：どの外注見積か（画面の「外注 沼田電気工事 N-2026-02」）。
   * 049：入れ方（mode）と元の行（sources）も。merge＝「3行から（現場雑費・現場管理費・諸経費）」、split＝「電工費 150,000 のうち」（docs/34 付録A）
   */
  costRef: z
    .object({
      vendorQuoteId: z.string().uuid(),
      vendorName: z.string(),
      quoteNo: z.string(),
      quotedOn: z.string().nullable(),
      /** 外注先の略称（#131。出どころのしるし。全部の名前は vendorName） */
      vendorShort: z.string().optional(),
      mode: z.enum(["into", "merge", "append", "split"]).nullable().default(null),
      /** 元の外注の行（税抜）。split の base＝外注の行の金額（この行の分は allocated） */
      sources: z.array(z.object({ vendorLineId: z.string().uuid(), name: z.string(), amount: Yen, base: Yen })).default([]),
    })
    .nullable()
    .default(null),
  /** この行にぶら下がる拾い出しの数（#130。メモだけのものも数える） */
  takeoffCount: z.number().int().default(0),
  /** 一式の印（#130）：breakdown＝内訳あり／memo＝根拠あり／none＝なし。単位が「式」のふつうの行だけ（ほかは null）。DB が決める */
  lumpsumBasis: LumpsumBasis.nullable().default(null),
  /** 行の系譜（document_lines.lineage_id、032）。写真・メモ・拾い出しはこれで行に付く（#117。回を重ねても・並べ替えても同じ）。見積の画面で読むときだけ入る */
  lineageId: z.string().uuid().optional(),
  /** この行に付いた写真の枚数（#117。社内用・紙に出ない）。見積だけ（請求書・領収書・雛形は 0） */
  photoCount: z.number().int().default(0),
  /** この行の社内メモの数（#117。社内用・紙に出ない）。見積だけ */
  memoCount: z.number().int().default(0),
});
export type EstimateLine = z.infer<typeof EstimateLine>;

/**
 * スマホの見積の数（#117、DB の v_estimate_phone_summary）。いま開いている回（下書きがあれば下書き）で数える。
 * 行数は見出しの行を除く。外注先 未定＝ふつうの行で材工区分が外注・外注先なし、または文章だけの行で仮（外注待ち）・外注先なし
 */
export const PhoneEstimateSummary = z.object({
  lineCount: z.number().int(),
  photoCount: z.number().int(),
  unassignedPhotoCount: z.number().int(),
  vendorMissingCount: z.number().int(),
  provisionalCount: z.number().int(),
  memoCount: z.number().int(),
});
export type PhoneEstimateSummary = z.infer<typeof PhoneEstimateSummary>;

/** PUT /estimates/:id/costs：仕入れ（社内用の列）だけ直す。出した回でも通る（047、docs/33 決めごと11） */
export const EstimateCostsUpdate = z.object({
  costs: z.array(z.object({ lineId: z.string().uuid(), costAmount: Yen.min(0).nullable() })).min(1).max(500),
  /** 版（#202）：Estimate.updatedAt。違えば 409 ESTIMATE_STALE。省けば見ない。書いたら版を進める */
  version: z.string().min(1).max(40).optional(),
});
export type EstimateCostsUpdate = z.infer<typeof EstimateCostsUpdate>;

export const EstimateStatus = z.enum(["draft", "issued", "sent", "paid", "lost", "voided"]);

/** 受注／失注の状態（024 fn_decide_quote）。none＝返事待ち（工事にまだ昇格していない） */
export const OrderState = z.object({
  status: z.enum(["none", "ordered", "lost"]),
  /** 受注日・失注日 */
  decidedAt: z.string().nullable(),
  /** 工事（projects）。受注・失注で昇格したときだけ */
  projectId: z.string().uuid().nullable(),
  /** この工事のいちばん新しい請求書（下書きも含む）。無ければ null */
  invoice: z.object({ id: z.string().uuid(), status: EstimateStatus, documentNo: z.string().nullable() }).nullable(),
  /** 出した請求書の数（請求済みの判定） */
  invoicedCount: z.number().int(),
  /** 「元に戻す」ができるか（請求書を起こす前だけ） */
  canUndecide: z.boolean(),
  /** 分割の予定（042、docs/30）。無ければ null＝一括。count＝回数、issuedCount＝出した回 */
  plan: PlanBrief.nullable().optional(),
});
export type OrderState = z.infer<typeof OrderState>;

export const Estimate = z.object({
  id: z.string().uuid(),
  documentNo: z.string().nullable(),
  status: EstimateStatus,
  client: z.object({
    id: z.string().uuid(),
    name: z.string(),
    honorific: z.string(),
    /** 宛先の支店・営業所（028）。無ければ null（本社）。紙の宛名は「会社名」の下に「支店名 御中」 */
    branch: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    /** 顧客担当者（039）。あれば紙の宛名は「会社名／支店名／部署 氏名 様」（御中は付けない） */
    contact: z.object({ id: z.string().uuid(), name: z.string(), department: z.string() }).nullable(),
  }),
  /** 案件（039 から見積は必ずどれかの案件に入る。古い形の読み込みのため型は nullable のまま） */
  projectGroup: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      siteAddress: z.string().nullable(),
      ownerName: z.string().nullable(),
      /** 039：この見積のために自動でできて、この見積だけが入っている案件（下書きを捨てると一緒に消える） */
      autoOnly: z.boolean().optional(),
      /** 039：名前を工事名に合わせているか（見積が1枚のあいだ） */
      nameFollowsSubject: z.boolean().optional(),
      /** 042：案件の請求タイミング（一括／分割）。受注のあと「分割予定作成」を出すかどうかに使う */
      billingTiming: BillingTiming.optional(),
    })
    .nullable(),
  revision: z.object({
    id: z.string().uuid(),
    no: z.number().int(),
    subject: z.string(),
    issueDate: z.string(),
    siteAddress: z.string(),
    notes: z.string(),
    workPeriodText: z.string(),
    workOutline: z.string(),
    validityText: z.string(),
    staffName: z.string(),
    /** 「訂正の内容」1行（2回目からの回だけ。発行時に凍結、紙の表紙に印字。032） */
    revisionNote: z.string(),
    /** 差し替えの一文（035）。2回目からの回を作るたびに自動で入る。直せる・消せる。紙では備考の上に出る。1回目は "" */
    replaceSentence: z.string(),
    /** 明細金額の丸め（数量×単価・率の行）と消費税の丸めのコード（向き×マイナスの基準、rounding.ts）。版に写して発行時に凍結 */
    lineRoundingMode: RoundingCode,
    roundingMode: RoundingCode,
    /** 税抜の対価の合計（立替金を含まない） */
    subtotal: Yen,
    taxTotal: Yen,
    /** 立替金の合計（T1）。subtotal・taxTotal に入らず total に入る */
    reimbursementTotal: Yen,
    /** total＝subtotal＋taxTotal＋reimbursementTotal */
    total: Yen,
    /** 帳票の取引日（T1。null なら発行日） */
    taxPointDate: z.string().nullable(),
  }),
  lines: z.array(EstimateLine),
  /** 内訳の紙（表紙が先頭）。1枚の見積は空。subtotal は DB が計算したその紙の税抜合計（表紙は表紙の行だけ） */
  sheets: z.array(SheetInput.extend({ no: z.number().int(), subtotal: Yen })),
  /** 出したあとの情報（下書きは null）。発行時点の自社名・登録番号と宛先の名前・敬称は snapshot に凍結 */
  issued: z
    .object({
      at: z.string(),
      issuerName: z.string(),
      issuerRegistrationNo: z.string().nullable(),
      recipientName: z.string(),
      recipientHonorific: z.string(),
      /** 出した時点の宛先の支店名（028。無ければ null） */
      recipientBranch: z.string().nullable(),
      /** 出した時点の宛名の担当者「部署 氏名」（039。無ければ null） */
      recipientContact: z.string().nullable(),
    })
    .nullable(),
  /** 出したあとに「取消して下書きに戻す」ができるか（会社の設定 allow_quote_unissue が ON で、status が issued のとき。021） */
  canUnissue: z.boolean(),
  /** 紙に出す番号（#27、032）。訂正版は枝番（Q-0012-2）。番号なしの下書きは null */
  displayNo: z.string().nullable(),
  /**
   * 「直して出し直す」を出せるか（出した見積で、直している下書きが無いとき）。
   * 履歴と差分のあるプラン（036）はいつでも。無いプランは、取消しの道が無いとき（受注のあと・設定 OFF）だけ
   */
  canRevise: z.boolean(),
  /** 見積の履歴と差分（回の帯・回をくらべる）が使えるプランか（036、PLAN_CATALOG.revisionHistory） */
  historyEnabled: z.boolean(),
  /** 訂正版の下書きを書いているとき（revision はその下書きの版）。base は顧客に出ている版 */
  revising: z
    .object({ baseRevisionNo: z.number().int(), baseIssueDate: z.string(), baseIssuedAt: z.string(), baseTotal: Yen })
    .nullable(),
  /**
   * 行・内訳の紙の系譜（id → lineage_id。037）。直しているときだけ入る。画面は覚えておいて、保存のとき送り返す
   * （消した内訳をトーストの「元に戻す」で戻したとき、同じ行だと分かるように。EstimateSave.lineage）
   */
  lineage: z.object({ lines: z.record(z.string(), z.string()), sheets: z.record(z.string(), z.string()) }).optional(),
  /** 差し替えられた旧い版（控えの PDF を見るためだけ。初版だけなら空） */
  history: z.array(z.object({ no: z.number().int(), issueDate: z.string(), total: Yen })),
  /** 受注／失注（024）。出したあとの見積だけ持つ。invoice はこの工事のいちばん新しい請求書（無ければ null） */
  order: OrderState.optional(),
  /**
   * 税率ごとの集計（document_tax_summaries）。消費税は (課税の別, 税率) ごとの合計に対して1回だけ丸める（T1）。
   * 立替金は kind=reimbursement の行（税 0）。画面・紙の消費税の行は isTaxRow で立替金を外す
   */
  taxes: z.array(TaxSummary),
  /** 紙の書式（#78、044）。下書きは 会社の既定 → 顧客ごとの既定 → この下書きで選んだもの、出したあとは固めたもの（frozen）。領収書と雛形には無い */
  paper: PaperInfo.optional(),
  /**
   * いまの端数処理（会社＋顧客、#108・docs/41）で作るとしたら回に写すコード。まだ出していない見積だけ持つ（請求書・領収書には無い）。
   * revision の lineRoundingMode・roundingMode と違えば、画面は「既定値で再計算」（POST /estimates/:id/rounding）を出す
   */
  roundingNow: z.object({ lineRoundingMode: RoundingCode, roundingMode: RoundingCode }).optional(),
  /** 紙の並べ方（#129。document_revisions.layout_axis）。省けば manual＝手で並べる（雛形・古い形の読み込み） */
  layoutAxis: LayoutAxis.optional(),
  /** 並べ方に使う工種・場所の名前と印刷の設定（#129）。出した回は固めた値 */
  axes: AxesInfo.optional(),
  /** 拾い出し（#130）：行ごとの件数と合計、行に入れていない件数。見積だけ（請求書・領収書・雛形には無い） */
  takeoffs: TakeoffSummary.optional(),
  /** 内訳のない一式（#130）：会社の基準を超えた行と金額の合計。社内用（紙に出ない・出すのは止めない）。見積だけ */
  lumpsumWarnings: LumpsumWarnings.optional(),
  /** スマホの見積の数（#117）：行・写真・行に入れていない写真・外注先 未定・仮・メモ。見積だけ */
  phoneSummary: PhoneEstimateSummary.optional(),
  /** 仕入れの集計（#131。社内用の小計の行の「仕入れ未入力 n行」）。見積だけ */
  costSummary: CostSummary.optional(),
  /** 積算表を付けるか（#131。見積ごとの選択 → 無ければ会社の設定）。まだ出していない見積だけ */
  takeoffSheet: TakeoffSheetInfo.optional(),
  /** 削除した内訳の数（#200、docs/81。内訳タブの帯の「削除した内訳 n」）。見積だけ */
  deletedSheetCount: z.number().int().optional(),
  /** 新しい行の仕入れの出どころの既定（#131 の2回目。設定 → 見積）：own＝自社／none＝未定。見積だけ（請求書・雛形には無い＝未定） */
  costDefault: CostDefault.optional(),
  /** パートナー（#213、docs/40 §4）。付けていなければ null。見積だけ。社内用（紙・送信・比較・見積依頼には出さない） */
  referral: ReferralInfo.nullable().optional(),
  /** 手数料の集計（#213）。パートナーが無ければ null。出した回は発行の瞬間に固めた値（frozen） */
  referralSummary: ReferralSummary.nullable().optional(),
  /** 粗利率の既定と上乗せ後の単価の丸め（#213。行の詳細の 粗利率・上乗せ・★B の自動の単価）。見積だけ */
  markup: MarkupSettings.optional(),
  /**
   * 取引日の欄を出すか（T2、§1-5）：帳票の日付から1年のうちに、この帳票で使っている区分の税率がマスタで変わる（fn_tax_rate_changes）か、
   * 取引日（版・行）がすでに入っているとき true。false の帳票は頭の欄・行の詳細に「取引日」を出さない。雛形・領収書には無い
   */
  taxDateHint: z.boolean().optional(),
  /**
   * 版（documents.updated_at）。行の中身が変わるたびに進む（写真・メモ・現場メモでは進まない）。
   * スマホの行ごとの保存（#117）はこの値を一緒に送り、違えば 409 ESTIMATE_STALE（PC の丸ごとの保存とぶつかった）
   */
  updatedAt: z.string(),
  /**
   * 最後に版を進めた利用者（documents.updated_by_user_id＝会社の中の人の id。#202 の直し、2026-10-18）。分からなければ null
   * （配備の前に書いた帳票・ログインの外の仕事）。PC の見積書は、読み直した版が新しくても、これが自分で送っていない修正が無ければ
   * 窓を出さずに取り直す。雛形・請求書のモードでは入らない
   */
  updatedBy: z.string().uuid().nullable().optional(),
});
export type Estimate = z.infer<typeof Estimate>;

/** 内訳の複製の答え（#199）：見積の全体＋新しい内訳の id。総額指定の値引きの行は写さない（1見積に1つ）＝写さなかったら skippedTargetDiscount */
export type SheetCopyResult = Estimate & { copiedSheetId: string; skippedTargetDiscount: boolean };

/** 削除した内訳・ゴミ箱を残す日数（#200、docs/81）。番号のある見積書はゴミ箱に残し続ける（消さない） */
export const TRASH_DAYS = 30;

/** 削除した内訳の1件（#200、docs/81。GET /estimates/:id/deleted-sheets） */
export const DeletedSheet = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** 削除したときに不使用だったか（復元しても不使用のまま） */
  inUse: z.boolean(),
  lineCount: z.number().int(),
  /** その内訳の小計（税抜） */
  subtotal: Yen,
  deletedAt: z.string(),
  /** 削除者の氏名（いなくなっていれば null） */
  deletedBy: z.string().nullable(),
  /** この日時を過ぎると、次の朝の掃除で消える（削除から TRASH_DAYS 日） */
  purgeAt: z.string(),
});
export type DeletedSheet = z.infer<typeof DeletedSheet>;
export const DeletedSheetList = z.object({
  items: z.array(DeletedSheet),
  /** 復元できないとき、その理由（one_sheet＝1枚の見積書・issued＝出した回を開いている）。できるときは null */
  blockedBy: z.enum(["one_sheet", "issued"]).nullable(),
});
export type DeletedSheetList = z.infer<typeof DeletedSheetList>;
/** POST /estimates/:id/deleted-sheets/:did/restore の本文。version は画面が読んだときの updatedAt（違えば 409 ESTIMATE_STALE） */
export const DeletedSheetRestore = z.object({ version: z.string().max(40).optional() });
export type DeletedSheetRestore = z.infer<typeof DeletedSheetRestore>;
/** 復元の答え：見積の全体＋戻した内訳の id */
export type SheetRestoreResult = Estimate & { restoredSheetId: string };

/** ゴミ箱の見積書の1件（#200、docs/81。GET /estimates/trash） */
export const TrashItem = z.object({
  id: z.string().uuid(),
  /** 番号（取消して下書きに戻した見積書だけ）。番号のある見積書は自動では消えない（人が完全に削除できる＝#271） */
  documentNo: z.string().nullable(),
  subject: z.string(),
  clientName: z.string(),
  branchName: z.string().nullable(),
  total: Yen,
  deletedAt: z.string(),
  deletedBy: z.string().nullable(),
  /** この日時を過ぎると次の朝の掃除で消える。番号のある見積書は null（自動では消えない） */
  purgeAt: z.string().nullable(),
});
export type TrashItem = z.infer<typeof TrashItem>;
export const TrashList = z.object({ items: z.array(TrashItem) });
export type TrashList = z.infer<typeof TrashList>;
/** DELETE /estimates/:id で1回目の下書きをゴミ箱に入れたときの答え（2回目からの下書きは今のまま 204） */
export const TrashResult = z.object({
  trashed: z.literal(true),
  documentNo: z.string().nullable(),
  /** 見積書と一緒にできた案件も一緒にゴミ箱に入ったか */
  groupTrashed: z.boolean(),
  groupName: z.string().nullable(),
});
export type TrashResult = z.infer<typeof TrashResult>;
/** POST /estimates/:id/untrash の答え */
export const UntrashResult = z.object({ id: z.string().uuid(), subject: z.string(), message: z.string() });
export type UntrashResult = z.infer<typeof UntrashResult>;
/** ゴミ箱の見積書を開いたとき（GET /estimates/:id の 404 ESTIMATE_TRASHED に付く） */
export const TrashedInfo = z.object({ deletedAt: z.string(), deletedBy: z.string().nullable(), documentNo: z.string().nullable() });
export type TrashedInfo = z.infer<typeof TrashedInfo>;

/** 消えるまでの日数（切り上げ。0 なら「まもなく削除」）。purgeAt が null（番号あり）なら null */
export function trashDaysLeft(purgeAt: string | null, now: Date = new Date()): number | null {
  if (purgeAt == null) return null;
  return Math.max(0, Math.ceil((new Date(purgeAt).getTime() - now.getTime()) / 86_400_000));
}

/** ゴミ箱・削除した内訳の文（#200、docs/81。docs/10 §5.1.2：削除・復元・削除者・削除日） */
export const TRASH_MSG = {
  tab: "ゴミ箱",
  /** 下書きの削除の窓の文は DISCARD_MSG（askWhole・askWholeWithGroup・askWholeNumbered）。スマホ版（ゴミ箱の一覧はパソコンだけ）はこれ */
  phoneAsk: "この見積書をゴミ箱に移します。30日以内ならパソコンのゴミ箱から復元できます",
  moved: "ゴミ箱に移しました",
  undo: "元に戻す",
  restore: "復元",
  restored: "「{subject}」を復元しました",
  show: "表示",
  left: "あと{n}日",
  soon: "まもなく削除",
  kept: "保存",
  /** #271（docs/81 §13）：番号のある見積書も「完全に削除」できる（人の操作だけ）。自動の掃除では消えない */
  keptHelp: "番号のある見積書は自動では消えず、いつでも復元できます",
  empty: "ゴミ箱は空です。削除した見積書は30日間ここに残ります",
  headDeletedAt: "削除日",
  headDeletedBy: "削除者",
  headLeft: "残り",
  /** ゴミ箱の見積書を開いたときの止めの画面の文は STOP_MSG.trashed（stop.ts） */
  notFound: "ゴミ箱にこの見積書はありません",
  /* ---- 完全に削除（#265、docs/81 §12 → #271 §13：番号ありにも・1件ずつ・確認の窓） ---- */
  /** ゴミ箱の行の文字のボタンと、確認の窓の題 */
  purge: "完全に削除",
  purgeAsk: "見積書「{subject}」を完全に削除します。復元できません",
  /** 番号のある見積書（取消して下書きに戻したもの）の確認の窓（#271）。{no}＝番号 */
  purgeAskNumbered: "見積書 {no}「{subject}」を完全に削除します。復元できません。番号 {no} は欠番になります",
  purgeConfirm: "削除",
  purgeCancel: "取消",
  purged: "「{subject}」を完全に削除しました",
  /** 出した回のある見積書を完全に削除しようとしたとき（API の 409 QUOTE_TRASH_KEPT。出した見積書はゴミ箱に入れられない＝ふつうは出ない。#271 で番号ありは消せるようにした） */
  purgeIssued: "出した見積書は完全に削除できません",
  /** {project}の画面の見積が0枚の表示の1行（ゴミ箱の一覧へ） */
  groupTrashLink: "ゴミ箱に見積書 {n}件",
} as const;

export const SHEET_TRASH_MSG = {
  /** 内訳タブの帯の文字のボタン「削除した内訳 n」と窓の題 */
  entry: "削除した内訳",
  title: "削除した内訳",
  restore: "復元",
  restored: "「{name}」を復元しました",
  oneSheet: "表紙＋内訳にすると復元できます",
  issued: "訂正版を作成すると復元できます",
  empty: "削除した内訳はありません",
  lines: "{n}行",
  /** 内訳の削除の窓と、そのあとの通知 */
  deleteAsk: "「{name}」を削除しますか",
  deleteBody: "この内訳の {n}行（{amount} 円）も一緒に削除します。「削除した内訳」から30日以内なら復元できます",
  deleted: "「{name}」を削除しました（{n}行）",
  /** 行の無い内訳（「削除した内訳」には入らない） */
  deleteEmpty: "この内訳には行がありません",
  already: "この内訳はもう見積書に戻っています",
  notFound: "削除した内訳が見つかりません",
  close: "閉じる",
} as const;

export const EstimateSummary = z.object({
  id: z.string().uuid(),
  documentNo: z.string().nullable(),
  status: EstimateStatus,
  subject: z.string(),
  clientId: z.string().uuid(),
  clientName: z.string(),
  honorific: z.string(),
  /** 宛先の支店・営業所の名前（028）。無ければ null */
  branchName: z.string().nullable(),
  /** 案件（現場）。無ければ null（工事名と同じ）。033：一覧の「案件：〇〇」を /groups/:id へのリンクに */
  groupId: z.string().uuid().nullable(),
  groupName: z.string().nullable(),
  /** 042：案件の請求タイミング（一括／分割）。分割の案件の受注した見積には「請求書をつくる」でなく「分割予定作成」を出す */
  groupBillingTiming: BillingTiming.optional(),
  /** #289（docs/101 §6）：継続案件か（一覧の2行目はいつも「{project}：名前 ›」） */
  groupIsOngoing: z.boolean().optional(),
  issueDate: z.string(),
  total: Yen,
  lineCount: z.number().int(),
  /** 出ている版の番号（#27。2以上なら一覧に枝番のチップ「-2」） */
  revisionNo: z.number().int(),
  /** 書きかけの訂正版があれば、その版の番号（チップ「-2 下書き」）。無ければ null */
  draftRevisionNo: z.number().int().nullable(),
  /** 受注／失注と請求の状態（024）。一覧の絞り込み（返事待ち／受注（未請求）／請求済み／失注）と行のボタンに使う */
  order: OrderState,
  /** スマホの一覧のカード（#117）：「行 n・写真 n」。GET /estimates?view=phone のときだけ入る */
  phone: PhoneEstimateSummary.optional(),
  updatedAt: z.string(),
});
export type EstimateSummary = z.infer<typeof EstimateSummary>;

/** 紙に出す番号（DB の fn_display_document_no と同じ）。訂正版は枝番（Q-0012-2）、初版は無印 */
export function displayDocumentNo(no: string | null, revisionNo: number): string | null {
  if (no == null) return null;
  return revisionNo >= 2 ? `${no}-${revisionNo}` : no;
}

/** 一覧の絞り込み（docs/10 §9.2「状態で絞るタブ」）。DB との対応は同じ表 */
export const EstimateFilter = z.enum(["all", "draft", "waiting", "ordered", "billed", "lost"]);
export type EstimateFilter = z.infer<typeof EstimateFilter>;
export const ESTIMATE_FILTER_LABEL: Record<EstimateFilter, string> = {
  all: "すべて",
  draft: "下書き",
  // #187（10/15 射場さん）：返事待ち → 未受注・受注（未請求）→ 受注・請求済み → 請求済（業務の言葉。コードは変えない）
  waiting: "未受注",
  ordered: "受注",
  billed: "請求済",
  lost: "失注",
};
/** 1件がどの絞り込みに入るか */
export function estimateFilterOf(x: { status: z.infer<typeof EstimateStatus>; order: OrderState }): Exclude<EstimateFilter, "all"> {
  if (x.status === "draft") return "draft";
  if (x.order.status === "lost" || x.status === "lost") return "lost";
  if (x.order.status === "ordered") {
    // 分割の予定があれば、すべての回を出すまで「受注」のまま（042、docs/30 決めごと7）
    const p = x.order.plan;
    if (p && p.count > 0) return p.issuedCount >= p.count ? "billed" : "ordered";
    return x.order.invoicedCount > 0 ? "billed" : "ordered";
  }
  return "waiting";
}
export const EstimateList = z.object({
  items: z.array(EstimateSummary),
  /** ゴミ箱の見積書の数（#200。一覧のタブ「ゴミ箱 n」。「すべて」には入れない） */
  trashCount: z.number().int().optional(),
});
export type EstimateList = z.infer<typeof EstimateList>;

/** ホームの下「今月の数字」の1か月分（#52、docs/10 §9.2）。金額は税込、利益だけ税抜 */
export const MonthFigures = z.object({
  /** "2026-09" */
  month: z.string(),
  /** 出した見積（current の版の issued_at がその月。取消しで下書きに戻した分は入らない） */
  issued: z.object({ count: z.number().int(), total: Yen }),
  /** 受注（projects.decided_at がその月・order_status = ordered） */
  ordered: z.object({ count: z.number().int(), total: Yen }),
  /** 失注（同・lost） */
  lost: z.object({ count: z.number().int(), total: Yen }),
  /**
   * 利益（その月に受注した分。税抜合計 − 仕入れ合計）。
   * 仕入れ未入力（cost_confidence = none のふつうの行）が1枚でもあれば amount・rate は null で missing に枚数（ルール7）。
   * 受注が 0 件のときも null（0円と紛れない）
   */
  profit: z.object({ amount: Yen.nullable(), rate: z.number().nullable(), missing: z.number().int() }),
  /** 未入金（E1、営業力 docs/08 決めごと13。sales の会社の今月だけ）：いまの残の合計と、そのうち期限超過。sales が OFF の会社は省く */
  unpaid: UnpaidFigures.optional(),
  /** 未払い（#233 3a。sales の会社の今月だけ）：確定した仕入の今の残の合計と、そのうち期限超過。sales が OFF の会社は省く */
  payable: UnpaidFigures.optional(),
  /** 支払（#233 3c。sales の会社だけ）：その月の支払日で登録した支払（取消を除く）の件数と額（振り込んだ額）の合計。sales が OFF の会社は省く */
  paid: z.object({ count: z.number().int(), total: Yen }).optional(),
});
export type MonthFigures = z.infer<typeof MonthFigures>;

/** ホーム「本日の業務」の数字（docs/10 §9.2 の対応表） */
export const HomeSummary = z.object({
  /** 下書き（documents.status = draft） */
  drafts: z.object({ count: z.number().int(), oldestIssueDate: z.string().nullable() }),
  /** 返事待ち（issued/sent で案件未決）。第1版はまだ発行が無いので 0 */
  waiting: z.object({ count: z.number().int(), total: Yen }),
  /** 受注して、出した請求書がまだ無い工事（024 v_project_invoice_state）。drafts＝そのうち請求書の下書きがある数 */
  toBill: z.object({ count: z.number().int(), total: Yen, drafts: z.number().int() }),
  /** 1枚でも見積があるか（無ければ「まず1枚」） */
  hasAny: z.boolean(),
  /** 今月・先月の数字。両方とも 0件 なら画面はこの段を出さない（空の数字を並べない） */
  months: z.object({ thisMonth: MonthFigures, lastMonth: MonthFigures }),
  /** 外注見積（047、docs/33）：確認待ち・読み取れなかった・読み取り中の数と、確認待ちの先頭（今日やること） */
  vendorQuotes: z.object({
    review: z.number().int(),
    failed: z.number().int(),
    reading: z.number().int(),
    items: z.array(z.object({ id: z.string().uuid(), vendorName: z.string(), quoteNo: z.string(), groupName: z.string(), status: z.enum(["review", "failed"]) })),
  }),
  /** 入金待ち（E1。sales の会社だけ）。期限超過が上、次に期限の近い順。sales が OFF の会社は省く */
  awaitingPayment: AwaitingPayment.optional(),
  /** 支払予定（#233 3a。sales の会社だけ）：確定した仕入のうち期日 7 日以内と期限超過。期限超過が上、次に期日の近い順。sales が OFF の会社は省く */
  purchasesDue: HomePurchasesDue.optional(),
  /** {vendor}の返事待ち（#119、docs/56 §6.6）：送付済で差し替え済でない見積依頼。画面は使う機能 vendorQuote の会社だけ・0件なら出さない */
  rfqWaiting: HomeRfqWaiting,
  /** メールの確認（#225 ★4）：届いていません・未取得 7日（確認済みは除く）。0件なら画面はカードを出さない */
  mailWatch: z.object({ bounced: z.number().int(), unopened: z.number().int() }).optional(),
  /**
   * 納品書（#266、docs/96）：未納品＝受注して残のある見積（見積から直接請求した・分割の受注は除く）の件数と残の金額（税抜）、
   * 納品済み・未請求＝発行済で請求書に載っていない納品書の枚数。sales の会社だけ（ほかは省く）。出すかは画面が使う機能「納品書」で決める
   */
  deliveryNotes: z.object({ undelivered: z.number().int(), restAmount: Yen, deliveredUnbilled: z.number().int() }).optional(),
  /** 締め（#267）：締め日が来て未請求の対象がある{customer}の数と月（「締め n社（10月分）」）。sales の会社で1社以上のときだけ */
  closingDue: HomeClosingDue.optional(),
  /** 定期請求（#268）：定期請求の下書きの数と、いちばん早い請求月（「定期 n件（下書き）」）。sales の会社で1件以上のときだけ */
  recurringDrafts: HomeRecurringDrafts.optional(),
});
export type HomeSummary = z.infer<typeof HomeSummary>;

/** 候補の引き出し（縦串3b、試作 v3.10.1）。前の見積の行と、会社設定の率の名前 */
export const SuggestItem = z.object({
  name: z.string(),
  spec: z.string(),
  unit: z.string(),
  unitPrice: Yen.nullable(),
  /** 最後に使ったときの仕入れ（分かっていたときだけ） */
  costAmount: Yen.nullable(),
  /** 使った回数 */
  count: z.number().int(),
  /** 最後に使った見積の工事名と見積日 */
  lastSubject: z.string(),
  lastIssueDate: z.string(),
  /** 最後に使ったときの税区分（T2。選ぶと行に写る） */
  taxCategory: TaxCategory.default("standard"),
});
export type SuggestItem = z.infer<typeof SuggestItem>;
export const RatePreset = z.object({
  name: z.string(),
  /** 0.1537 = 15.37% */
  rate: z.number(),
  rateBase: RateBase,
  costKind: CostKind.nullable(),
});
export type RatePreset = z.infer<typeof RatePreset>;
/** 品目マスタの候補（T2：候補の引き出しの「品目」。選ぶと 名称・単位・税区分・品目 が行に写る） */
export const SuggestMasterItem = z.object({ id: z.string().uuid(), name: z.string(), unit: z.string(), taxCategory: TaxCategory });
export type SuggestMasterItem = z.infer<typeof SuggestMasterItem>;
export const Suggest = z.object({ past: z.array(SuggestItem), rates: z.array(RatePreset), items: z.array(SuggestMasterItem).default([]) });
export type Suggest = z.infer<typeof Suggest>;

/**
 * 候補の引き出しの文（#215、2026-10-19 射場さん。Figma「候補の引き出し（10/19）」193:54）。
 * 画面の components/Picker.tsx とスマホの候補のシート（PHONE_MSG.candidatesHere・candidatesPast）が同じ言葉を見る。
 * 引き出しの中に操作の説明の文を並べない（docs/10 §5.1.1）＝選び方は「？」の help へ
 */
export const PICKER_MSG = {
  title: "候補",
  tabs: { all: "すべて", here: "この見積", past: "過去の見積", item: "品目", rate: "率の名前" },
  /** 並びの1行（すべて・過去の見積のタブ） */
  sort: "使用回数の多い順",
  /** 打ち込み中の検索欄の右 */
  count: "{n}件",
  multi: "続けて選ぶ",
  helpLabel: "候補の選び方",
  help: "候補を押すか、↑↓ で選び Enter で行に反映します。「続けて選ぶ」をオンにすると、選ぶたびに下の行へ追加し、候補は開いたままです。過去の見積：ほかの見積書の{lineName}（下書きを含む）。品目：品目マスタ。率の名前：率の行で使った{lineName}。",
  search: "{lineName}・{lineSpec}で検索",
  searchLabel: "候補を探す",
  none: "該当する候補はありません。入力した{lineName}をそのまま使用できます。",
  empty: "候補はありません。",
  loading: "検索中…",
  close: "閉じる",
  /** 候補の行の下段（薄墨 14px の1行） */
  times: "{n}回",
  last: "前回 {d}",
  cost: "仕入れ {v}",
  base: "基礎：{b}",
  unit: "単位 {u}",
  tax: "税区分 {t}",
  noPrice: "単価なし",
  /** 名称の右の小さなしるし */
  chipItem: "品目",
  chipRate: "率",
  chipHere: "この見積",
} as const;

// ---------------------------------------------------------------------------
// 顧客に出す（縦串4a、020）。初回だけ会社名（住所・電話・登録番号は空でも出せる）。番号は出す瞬間に付く
// ---------------------------------------------------------------------------

export const ISSUE_MSG = {
  companyRequired: COMPANY_MSG.nameRequired,
  companyTooLong: COMPANY_MSG.nameTooLong,
  noLines: "明細が1行もありません。行を追加してから出してください",
  notDraft: "この見積はもう出したあとです",
  regNoInvalid: COMPANY_MSG.regNoInvalid,
} as const;


export const UNISSUE_MSG = {
  notIssued: "出したあとの見積だけ取り消せます",
  disabled: "出した見積の取消しは、設定で止めています。修正するときは「訂正版作成」を使います",
} as const;

/**
 * 取消しの窓（見積書・請求書・領収書で共用。#188、Figma 173:118）：題に警告のしるし＋1文だけ。本文の箇条書きは無くし、説明は「？」。
 * 2回目からの見積は回と枝番で「2回目「Q-0005-2」を…」
 */
export const UNISSUE_DIALOG_MSG = {
  /** 窓の名前（開くボタン「取り消して下書きに戻す」と同じ） */
  name: "取り消して下書きに戻す",
  title: "{doc}「{no}」を取消して下書きへ戻します。",
  roundTitle: "{round}回目「{no}」を取消して下書きへ戻します。",
  help: "発行済の{doc}を下書きへ戻します。番号はそのまま、PDF は再発行時に作成します。",
  helpLabel: "取消しの説明",
  warn: "警告",
  close: "閉じる",
  confirm: "下書きに戻す",
  /** {order}は会社の言葉（画面は t() を通す） */
  doc: { quote: "見積書", invoice: "請求書", receipt: "領収書", order: "{order}", payment_notice: "{paymentNotice}", delivery_note: "{deliveryNote}" },
} as const;
/** 取消しの窓の帳票の種類 */
export type UnissueDoc = keyof typeof UNISSUE_DIALOG_MSG.doc;

export const UnissueResult = z.object({
  documentNo: z.string(),
  /**
   * 取消しのあとの版（documents.updated_at。Estimate.updatedAt と同じ形）。画面は自分の操作の版として覚え、読み直した帳票の版がこれなら
   * 窓を出さずに取り直す（#202 の直し、2026-10-18：前は見積書の画面が発行の前の版のまま次の保存を送り、自分の取消しに 409 を返されて
   * 「他の利用者が変更しました」が出た）
   */
  updatedAt: z.string(),
});
export type UnissueResult = z.infer<typeof UnissueResult>;


/** 「顧客に出す」の中身。初回は company を一緒に送る（2枚目からは要らない）。訂正版は 訂正の内容＋変更点のページ（#27） */
export const IssueRequest = z.object({
  company: CompanyUpdate.optional(),
  /** 訂正版だけ：「訂正の内容」1行（自動の下書きを直しても、そのままでも）。空は出せない */
  revisionNote: z.string().trim().max(200).optional(),
  /** 訂正版だけ：最後のページに「変更点」の表を付けるか（この顧客の次回の既定として覚える） */
  changeSheet: z.boolean().optional(),
});
export type IssueRequest = z.infer<typeof IssueRequest>;

export const IssueResult = z.object({
  documentNo: z.string(),
  issuedAt: z.string(),
  /** 訂正版のとき、紙に出す番号（Q-0012-2） */
  displayNo: z.string().optional(),
  /** 発行のあとの版（documents.updated_at。Estimate.updatedAt と同じ形）。UnissueResult.updatedAt と同じ使い方（#202 の直し） */
  updatedAt: z.string(),
});
export type IssueResult = z.infer<typeof IssueResult>;

// ---------------------------------------------------------------------------
// 見積の「回」（#27・#75、032・035、docs/27）。出すたびに1回（Q-0002 → Q-0002-2 → Q-0002-3）。状態は回ごと。
// 差分（見くらべ・変更点のページ）は v_revision_diff（fn_revision_diff）が唯一の出どころ
// ---------------------------------------------------------------------------

/** {customer} はその会社の言葉に置き換わる（API は fillLabels、画面は t()） */
export const REVISE_MSG = {
  notIssued: "出したあとの見積だけ、修正して出し直せます",
  draftExists: "修正中の下書きがあります。そちらをひらきます",
  noDiff: "変わっているところがありません。修正してから出してください",
  noteRequired: "「訂正の内容」を1行書いてください",
  hasRevisionOnUnissue: "修正中の下書きがあります。先に「下書き削除」を押してください",
  clientLocked: "出したあとの見積は、{customer}を変えられません。別の{customer}に出すときは新しい見積をつくります",
  notRevising: "修正中の下書きがありません",
  tooMany: "1つの見積を修正できるのは 99回目までです。新しい見積をつくってください",
} as const;

/** プランに無い機能を呼んだとき（403 PLAN_FEATURE。036） */
export const PLAN_FEATURE_MSG = {
  revisionHistory: "見積の履歴と差分（見積書の比較）は、スモール・ビジネスのプランで使えます",
  reviseNeedsHistory: "このプランでは、出した見積は「取り消して下書きに戻す」で修正します（前の内容を残して出し直すのは、スモール・ビジネスのプランです）",
  /** #213（docs/40 §9）：1回目は全プランで利用できる。運営の管理画面でプランごとに閉じたときの断り */
  referralFee: "{referrer}手数料は、このプランでは利用できません",
} as const;

/** 「この下書きを捨てる」の文（035）。捨てるのは その回の下書きだけ。1回目の下書きのときだけ見積ごと消える */
export const DISCARD_MSG = {
  /** 1回目の下書き（まだ何も出していない見積・1回目を取り消した下書き） */
  askWhole: "この見積書をゴミ箱に移しますか。30日以内ならゴミ箱から復元できます",
  /** 039：この見積のために自動でできた案件は、一緒にゴミ箱に移る（#200。メニューから作った案件は残る）。{name}＝案件名 */
  askWholeWithGroup: "この見積書をゴミ箱に移しますか。30日以内ならゴミ箱から復元できます。一緒にできた{project}「{name}」もゴミ箱に移り、復元すると戻ります",
  /** #200：取消して下書きに戻した見積書（番号あり）。{no}＝番号 */
  askWholeNumbered: "この見積書（{no}）をゴミ箱に移しますか。番号のある見積書はゴミ箱に残り、いつでも復元できます",
  /** 2回目からの下書き。{n}＝その回、{prev}＝出ている回、{no}＝出ている回の番号（Q-0002） */
  askRevision: "{n}回目の下書きを捨てますか。{customer}に出ている {prev}回目（{no}）はそのまま残ります",
  /** 出した回しか無い見積は捨てられない */
  nothingToDiscard: "この見積には、捨てられる下書きがありません",
} as const;

/** 帳票の下書き削除の窓（{deliveryNote}・{order}・{paymentNotice}。画面の components/DeleteDraftDialog.tsx。docs/96 §13.5） */
export const DRAFT_DELETE_MSG = {
  title: "下書き削除",
  ask: "この下書きを削除します。元に戻せません。",
  confirm: "削除",
  cancel: "取消",
} as const;

/** 頭の欄の名前（見くらべ・変更点のページ）。{subject} などは会社の言葉に置き換わる */
export const REVISION_HEAD_FIELDS = {
  subject: "{subject}",
  work_period_text: "{period}",
  site_address: "{site}",
  validity_text: "有効期限",
  work_outline: "{outline}",
  staff_name: "担当者",
  notes: "備考",
} as const;

/** 差分の1行（見くらべ・変更点のページ）。changed は fields に 旧 → 新 の欄が入る */
export const RevisionDiffRow = z.object({
  /** head＝頭の欄（件名・工期・工事場所・有効期限・工事概要・担当者・備考。035）。name に欄の名前 */
  kind: z.enum(["line", "sheet", "head"]),
  /** calc＝自分の欄は同じで金額だけ変わった行（つられた計算。036）。fields は「金額」の1つ */
  change: z.enum(["added", "removed", "changed", "calc"]),
  /** 新しい名前（消した行・紙は旧い名前） */
  name: z.string(),
  /** どの紙の行か（1枚の見積・表紙は ""） */
  sheetName: z.string(),
  oldAmount: Yen.nullable(),
  newAmount: Yen.nullable(),
  /** 変えた欄（label は「数量」「単価」など。added / removed は空）。内訳の 使う／不使用 の変化は label が "" で「不使用 → 使用」（#199） */
  fields: z.array(z.object({ label: z.string(), old: z.string(), new: z.string() })),
  /** 内訳の紙（kind=sheet）の 使う／不使用（#199。片方の回に無ければ null）。紙（PDF）では 不使用 → 使用 を「追加」、使用 → 不使用 を「取りやめ」と書く */
  inUse: z.object({ old: z.boolean().nullable(), new: z.boolean().nullable() }).optional(),
});
export type RevisionDiffRow = z.infer<typeof RevisionDiffRow>;

/** 内訳の 使う／不使用 の言葉（#199。差分の「不使用 → 使用」） */
export const SHEET_USE_LABEL = { used: "使用", unused: "不使用" } as const;

export const RevisionDiff = z.object({
  /** 顧客に出ている版 */
  base: z.object({ revisionNo: z.number().int(), issueDate: z.string(), total: Yen, subtotal: Yen }),
  /** 書いている訂正版 */
  next: z.object({ revisionNo: z.number().int(), total: Yen, subtotal: Yen }),
  rows: z.array(RevisionDiffRow),
  /** 変わっていない行の数（たたむ側） */
  unchangedCount: z.number().int(),
  /** 「訂正の内容」の自動の下書き（差分から作る。直せる） */
  autoNote: z.string(),
  /** 社内用：仕入れの合計（未入力の行があれば null。ルール7） */
  cost: z.object({ old: Yen.nullable(), new: Yen.nullable() }),
  /** 「変更点」のページの既定：受注後はオン、それ以外はこの顧客の前回の選択（docs/18 3.4） */
  changeSheetDefault: z.boolean(),
});
export type RevisionDiff = z.infer<typeof RevisionDiff>;

// ---------------------------------------------------------------------------
// 回の帯と、回をくらべる画面（036、docs/27 の2回目）。スモール・ビジネスのプランだけ（403 PLAN_FEATURE）
// ---------------------------------------------------------------------------

/** 回の状態：下書き／発行済（出ている回）／前の回（差し替えられた） */
export const RoundState = z.enum(["draft", "current", "previous"]);
export type RoundState = z.infer<typeof RoundState>;
/** #188：出ている回は「発行済」（前は「出ている回」） */
export const ROUND_STATE_LABEL: Record<RoundState, string> = { draft: "下書き", current: "発行済", previous: "前の回" };

/** 回の帯（#188、Figma 173:99）：右端の ⌃／⌄ で畳む（端末に覚える gb.est.rounds） */
export const ROUND_MSG = {
  strip: "この見積の回",
  compare: "比較",
  compareTitle: "前の回と比較",
  copyPdf: "控え PDF",
  collapse: "回の帯を畳む",
  expand: "回の帯を広げる",
  /** 回が3つ以上のときのチップの列の左右の ‹ ›（#190） */
  scrollPrev: "前の回を表示",
  scrollNext: "次の回を表示",
} as const;

/** 見積書の比較（#188、Figma 173:352。前の題は「回の比較」） */
export const COMPARE_MSG = {
  title: "見積書の比較",
  help: "どの回とどの回でも比較できます。",
  from: "比較元の回",
  to: "比較先の回",
  onlyChanged: "変更行のみ表示",
  internal: "社内用",
  total: "合計（税込）",
  subtotal: "小計",
  tax: "消費税",
  reimbursement: "立替金",
  ex: "税抜",
  cover: "表紙",
  sheets: "内訳の紙",
  sheetCol: "内訳",
  changedLines: "変更行",
  noChange: "変更はありません。",
  costMissing: "仕入れ未入力の行があるので、利益は出しません",
  under: "原価割れ",
  note: "{n}回目の訂正の内容：{note}",
  /** 印（色だけで伝えない）。消した行は「削除」（前は「消した」） */
  change: { added: "追加", changed: "変更", removed: "削除", calc: "計算", cost: "仕入れ" },
} as const;

export const RevisionRound = z.object({
  no: z.number().int(),
  /** 紙に出す番号（Q-0002-2）。番号の無い下書きは null */
  displayNo: z.string().nullable(),
  state: RoundState,
  issueDate: z.string(),
  total: Yen,
  /** 前の回からの増減（税込）。1回目は null */
  deltaFromPrev: z.number().int().nullable(),
  /** 「訂正の内容」（2回目から。下書きは ""） */
  revisionNote: z.string(),
  /** 控えの PDF があるか（前の回だけ意味がある。GET /estimates/:id/pdf?rev=N） */
  hasCopy: z.boolean(),
  /** {order}の回の承諾の記録（#233 2b。見積書には無い）：方法・記録番号・日時（紙で回収は受け取った日）・請書の写しがあるか */
  ack: z.object({ method: z.enum(["screen", "paper"]), recordNo: z.string(), at: z.string(), hasCopy: z.boolean() }).nullable().optional(),
});
export type RevisionRound = z.infer<typeof RevisionRound>;
export const RevisionRounds = z.object({ rounds: z.array(RevisionRound) });
export type RevisionRounds = z.infer<typeof RevisionRounds>;

const pair = <T extends z.ZodTypeAny>(t: T) => z.object({ old: t, new: t });

/** くらべる画面の1行。same は old と new が同じ。added は old が空、removed は new が空 */
export const CompareLine = z.object({
  /** cost＝仕入れだけを変えた行（社内用の列にだけ違いが出る） */
  change: z.enum(["same", "changed", "added", "removed", "calc", "cost"]),
  role: RowRole,
  /** どの紙の行か（いまの紙の名前。消した行は前の紙の名前。1枚の見積は ""） */
  sheetName: z.string(),
  /** 紙を移した行：前にあった紙の名前（移していなければ null） */
  movedFrom: z.string().nullable(),
  name: pair(z.string()),
  spec: pair(z.string()),
  note: pair(z.string()),
  quantity: pair(z.number().nullable()),
  unit: pair(z.string()),
  unitPrice: pair(Yen.nullable()),
  /** 金額（税抜）。別途・見出し・文章は 0 */
  amount: pair(Yen.nullable()),
  rate: pair(z.number().nullable()),
  rateBase: pair(RateBase.nullable()),
  amountLabel: pair(z.string().nullable()),
  /** 社内用：仕入れ（未入力は null。ルール7） */
  cost: pair(Yen.nullable()),
  /** どの紙の行か（紙の系譜。sheets[].key と同じ。1枚の見積は null。#188 の内訳タブで紙ごとに分ける＝名前を変えた紙・同じ名前の紙でも混ざらない） */
  sheetKey: z.string().nullable(),
});
export type CompareLine = z.infer<typeof CompareLine>;

/** 回の合計（DB が持っている値。taxTotal・reimbursementTotal は #188 の比較の画面の下の 小計 → 消費税 → 合計 のため） */
const CompareSide = z.object({ no: z.number().int(), displayNo: z.string().nullable(), state: RoundState, issueDate: z.string(), subtotal: Yen, taxTotal: Yen, reimbursementTotal: Yen, total: Yen });

export const RevisionCompare = z.object({
  /** 見積の名前（比較先の回の件名）と番号（枝番なし。下書きだけの見積は null）。#188：題の右のチップ */
  subject: z.string(),
  documentNo: z.string().nullable(),
  from: CompareSide,
  to: CompareSide,
  /** 頭の欄（件名・工期・工事場所・有効期限・工事概要・担当者・備考）の変更 */
  heads: z.array(z.object({ label: z.string(), old: z.string(), new: z.string() })),
  /** 内訳の紙ごとの増減（1枚の見積は空）。小計は DB が計算した値。key＝紙の系譜、type＝表紙（cover）か内訳（breakdown） */
  sheets: z.array(z.object({
    key: z.string(), type: z.enum(["cover", "breakdown"]), name: z.string(), oldSubtotal: Yen.nullable(), newSubtotal: Yen.nullable(), changedCount: z.number().int(),
    /** 使う／不使用（#199。その回に無ければ null）。不使用の内訳は合計に入らない＝表紙の増減の表では「不使用」と書き、中の行は比べない */
    inUse: z.object({ old: z.boolean().nullable(), new: z.boolean().nullable() }),
  })),
  /** 明細。いまの並び順。消した行は元の場所の近く */
  lines: z.array(CompareLine),
  counts: z.object({ changed: z.number().int(), added: z.number().int(), removed: z.number().int(), calc: z.number().int(), same: z.number().int() }),
  /** 社内用：仕入れの合計（未入力の行があれば null。ルール7） */
  cost: pair(Yen.nullable()),
  /** to の回の「訂正の内容」 */
  revisionNote: z.string(),
});
export type RevisionCompare = z.infer<typeof RevisionCompare>;

// ---------------------------------------------------------------------------
// 直しているときの印と「戻す」（037、docs/27 の3回目）。スモール・ビジネスのプランだけ（403 PLAN_FEATURE）。
// 出どころは DB の差分（fn_revision_diff：出ている回 と 直している下書き）。画面は計算しない。保存のたびに取り直す
// ---------------------------------------------------------------------------

/** 「前は …」を出す欄（明細の表の列） */
export const MarkCell = z.enum(["name", "spec", "quantity", "unit", "unitPrice", "amount", "note"]);
export type MarkCell = z.infer<typeof MarkCell>;

/** いまの下書きにある行の印。added＝追加／changed＝変更／calc＝つられて金額だけ変わった */
export const RevisionMarkLine = z.object({
  /** 下書きの行の id（エディタの行の id と同じ） */
  lineId: z.string().uuid(),
  /** 行の系譜（「前の回に戻す」で送る） */
  lineageId: z.string().uuid(),
  change: z.enum(["added", "changed", "calc"]),
  /**
   * 変わった欄の 前の回の値（そのまま出せる文字。「前は 」は画面が付ける）。added は空。
   * cell＝どの列の下に出すか。label＝欄の名前（行を書いているときの帯で「数量 320」のように並べる。text だけで分かるものは ""）
   */
  was: z.array(z.object({ cell: MarkCell, label: z.string(), text: z.string() })),
});
export type RevisionMarkLine = z.infer<typeof RevisionMarkLine>;

/** 消した行（前の回にあって、下書きに無い）。元の場所に薄く出す */
export const RevisionMarkRemoved = z.object({
  /** 「戻す」で送る */
  lineageId: z.string().uuid(),
  /** どの紙に出すか（いまの下書きの紙の id。1枚の見積は null） */
  sheetId: z.string().uuid().nullable(),
  /** この行の次に出す（前の回で直前にあって、いまも同じ紙にある行）。無ければ紙のいちばん上 */
  afterLineId: z.string().uuid().nullable(),
  role: RowRole,
  name: z.string(),
  spec: z.string(),
  note: z.string(),
  /** そのまま出せる文字（DB の値を API が整形。画面は計算しない） */
  quantityText: z.string(),
  unit: z.string(),
  unitPriceText: z.string(),
  /** 金額の欄（別途の行は「別途」など。見出し・文章は ""） */
  amountText: z.string(),
});
export type RevisionMarkRemoved = z.infer<typeof RevisionMarkRemoved>;

/** 消した内訳の紙。表紙に薄く出す（行は1つずつ出さない。「戻す」で 紙ごと・行ごと 戻る） */
export const RevisionMarkRemovedSheet = z.object({
  lineageId: z.string().uuid(),
  name: z.string(),
  /** 前の回の この内訳の合計 */
  subtotal: Yen,
  /** 一緒に戻る行の数 */
  lineCount: z.number().int(),
  /** この内訳の次に出す（無ければ表紙のいちばん上） */
  afterSheetId: z.string().uuid().nullable(),
});
export type RevisionMarkRemovedSheet = z.infer<typeof RevisionMarkRemovedSheet>;

export const RevisionMarks = z.object({
  /** 顧客に出ている回 */
  base: z.object({ revisionNo: z.number().int(), total: Yen }),
  /** 直している下書き */
  next: z.object({ revisionNo: z.number().int(), total: Yen }),
  /** 行の数。removed は 消した内訳の紙の行も含む */
  counts: z.object({ changed: z.number().int(), added: z.number().int(), removed: z.number().int(), calc: z.number().int() }),
  /** タブの数：紙の id → その紙の 変更＋追加＋削除（消した内訳の行は表紙に数える）。1枚の見積は空 */
  sheetCounts: z.record(z.string(), z.number().int()),
  lines: z.array(RevisionMarkLine),
  removed: z.array(RevisionMarkRemoved),
  removedSheets: z.array(RevisionMarkRemovedSheet),
});
export type RevisionMarks = z.infer<typeof RevisionMarks>;

/**
 * 「戻す」「前回の内容に復元」（POST /estimates/:id/restore）。
 * line：下書きに無い系譜＝消した行を 同じ系譜で元の場所へ入れ直す／下書きにある系譜＝紙に出る欄を前の回の値に戻す。
 * sheet：消した内訳の紙を、行ごと戻す。どれも仕入れ・労務費の印は いまのまま
 */
export const RevisionRestore = z.object({
  kind: z.enum(["line", "sheet"]).default("line"),
  lineageId: z.string().uuid(),
});
export type RevisionRestore = z.infer<typeof RevisionRestore>;

export const RESTORE_MSG = {
  notFound: "前回に、その行がありません",
  sheetGone: "この行のあった内訳の紙が消されています。表紙で、先に内訳を戻してください",
  sheetNotRemoved: "その内訳は消されていません",
  noSheets: "いまは1枚の紙にまとめているので、内訳の紙は戻せません",
  unsaved: "保存できていない変更があります。修正してから、もう一度押してください",
  restored: "消した行を戻しました",
  reverted: "前回の内容に復元しました",
  sheetRestored: "消した内訳を戻しました",
} as const;

/** 印の言葉（チップ）。色だけにしない（docs/10） */
/** 直しているときの行・内訳の印（#188 の続きで「消した」→「削除」＝見積書の比較の印 COMPARE_MSG.change とそろえた） */
export const MARK_LABEL = { added: "追加", changed: "変更", removed: "削除", calc: "計算" } as const;
